import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Center,
  Flex,
  HStack,
  Icon,
  Stack,
  useToast,
} from '@chakra-ui/react';
import { Order } from '@diamond/shared/types';
import { Button, showToast, Text } from '@diamond/shared/ui';
import { buyerCaptureException, trackEvent } from '@diamond/shared/utils';
import { TRACKING_EVENTS } from '@diamond/sol-buyer/constant';
import { applyPaymentInstructionSentryContext } from '@diamond/sol-buyer/utils';
import CheckCircle from '@mui/icons-material/CheckCircle';
import ContentCopyOutlined from '@mui/icons-material/ContentCopyOutlined';
import { format } from 'date-fns';
import { id } from 'date-fns/locale';
import NextImage from 'next/image';
import {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

type PaymentInstructionOrder = Pick<
  Order,
  | 'bank_account'
  | 'payment'
  | 'payment_method'
  | 'payment_sub_state'
  | 'transaction_date'
  | 'transaction_id'
>;

interface PaymentInstructionScreenProps {
  analyticsContext: {
    ffPaymentInstructionPage: boolean;
    orderMetaId: string;
    warehouse: string;
  };
  isUploadDisabled: boolean;
  onDetailOrder: () => void;
  onExpired: () => void;
  onUpload: () => void;
  order: PaymentInstructionOrder;
  proofPanel?: ReactNode;
  showProofPanel?: boolean;
}

interface CopyButtonProps {
  label: string;
  onCopy: () => void;
}

const BRAND_GRADIENT = 'linear(to-r, #FF1E1E, #2451CE 70%, #5AC8FF)';
const TEXT_PRIMARY = '#373F41';
const TEXT_MUTED = '#8E8E93';
const ACCENT_BLUE = '#0941A8';
const ALERT_RED = '#FF0000';
const SALIN_BG = '#F2F2F7';

const normalizeTimestampMs = (timestamp: number) =>
  timestamp < 1_000_000_000_000 ? timestamp * 1000 : timestamp;

const formatTransactionDate = (timestamp: number) =>
  format(normalizeTimestampMs(timestamp), 'dd MMM yyyy • HH:mm', {
    locale: id,
  });

const formatRemainingTransferWindow = (distanceMs: number) => {
  if (distanceMs <= 0) return '';

  const totalMinutes = Math.floor(distanceMs / (1000 * 60));
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (hours <= 0) {
    return `${minutes} menit`;
  }

  return `${hours} Jam ${minutes} menit`;
};

const formatAmountDigits = (value: number) =>
  Math.abs(value).toLocaleString('id-ID').replace(/,/g, '.');

const resolveBankLogo = (bankName: string) => {
  const normalizedName = bankName.toLowerCase();
  if (normalizedName.includes('mandiri')) return '/images/mandiri.png';
  if (normalizedName.includes('bni')) return '/images/bni.png';
  return '/images/bca.png';
};

const CopyButton = ({ label, onCopy }: CopyButtonProps) => (
  <Button
    aria-label={label}
    bg={SALIN_BG}
    border="none"
    borderRadius="4px"
    color={ACCENT_BLUE}
    flexShrink={0}
    fontSize="14px"
    fontWeight={400}
    h="36px"
    minW={{ base: '36px', md: '80px' }}
    px={{ base: 2, md: 3 }}
    variant="solid"
    onClick={onCopy}
    _active={{ bg: SALIN_BG }}
    _hover={{ bg: SALIN_BG }}
  >
    <Icon as={ContentCopyOutlined} boxSize="20px" />
    <Box as="span" display={{ base: 'none', md: 'inline' }} ml={1}>
      Salin
    </Box>
  </Button>
);

export const PaymentInstructionScreen = ({
  analyticsContext,
  isUploadDisabled,
  onDetailOrder,
  onExpired,
  onUpload,
  order,
  proofPanel,
  showProofPanel = false,
}: PaymentInstructionScreenProps) => {
  const toast = useToast();
  const expiryReportedRef = useRef(false);
  const [currentTime, setCurrentTime] = useState(Date.now());

  const payment = order.payment;
  const bankAccount = order.bank_account;
  const expiryMs = payment
    ? normalizeTimestampMs(payment.expired_at)
    : currentTime;
  const isExpired = currentTime >= expiryMs;
  const remainingLabel = useMemo(
    () => formatRemainingTransferWindow(expiryMs - currentTime),
    [currentTime, expiryMs]
  );

  useEffect(() => {
    if (isExpired) return undefined;

    const intervalId = window.setInterval(() => {
      setCurrentTime(Date.now());
    }, 1000);

    return () => window.clearInterval(intervalId);
  }, [isExpired]);

  useEffect(() => {
    if (!isExpired || expiryReportedRef.current) return;
    expiryReportedRef.current = true;
    onExpired();
  }, [isExpired, onExpired]);

  useEffect(() => {
    if (bankAccount) return;

    buyerCaptureException(
      {
        message: 'Payment instruction destination bank account is missing',
        transaction_id: order.transaction_id,
      },
      (scope) => {
        applyPaymentInstructionSentryContext(scope, {
          orderMetaId: analyticsContext.orderMetaId,
          paymentSubState: order.payment_sub_state,
          reason: 'missing-destination-bank',
          source: 'instruction',
          warehouse: analyticsContext.warehouse,
        });
      }
    );
  }, [
    analyticsContext.orderMetaId,
    analyticsContext.warehouse,
    bankAccount,
    order.payment_sub_state,
    order.transaction_id,
  ]);

  const copyValue = useCallback(
    async (label: string, value: string) => {
      try {
        await navigator.clipboard.writeText(value);
        if (label === 'Nomor rekening') {
          trackEvent(TRACKING_EVENTS.CLICK_COPY_BANK_ACCOUNT, {
            entry_source: 'payment-instruction',
            ff_payment_instruction_page:
              analyticsContext.ffPaymentInstructionPage,
            order_meta_id: analyticsContext.orderMetaId,
            payment_method: order.payment_method,
            platform: 'web',
            resolved_destination: 'payment_instruction',
            warehouse: analyticsContext.warehouse,
          });
        }
        if (label === 'Total') {
          trackEvent(TRACKING_EVENTS.CLICK_COPY_AMOUNT, {
            entry_source: 'payment-instruction',
            ff_payment_instruction_page:
              analyticsContext.ffPaymentInstructionPage,
            order_meta_id: analyticsContext.orderMetaId,
            payment_method: order.payment_method,
            platform: 'web',
            resolved_destination: 'payment_instruction',
            warehouse: analyticsContext.warehouse,
          });
        }
        showToast(toast, 'success', `${label} berhasil disalin`);
      } catch (error: unknown) {
        buyerCaptureException(
          {
            error: error instanceof Error ? error.message : String(error),
            message: 'Payment instruction clipboard copy failed',
          },
          (scope) => {
            applyPaymentInstructionSentryContext(scope, {
              orderMetaId: analyticsContext.orderMetaId,
              paymentSubState: order.payment_sub_state,
              reason: 'clipboard-rejected',
              source: 'instruction',
              warehouse: analyticsContext.warehouse,
            });
            scope.setContext('clipboard', { label });
          }
        );
        showToast(toast, 'error', `${label} tidak dapat disalin`);
      }
    },
    [
      analyticsContext.ffPaymentInstructionPage,
      analyticsContext.orderMetaId,
      analyticsContext.warehouse,
      order.payment_method,
      order.payment_sub_state,
      toast,
    ]
  );

  const amountDigits = useMemo(
    () =>
      payment?.total !== null && payment?.total !== undefined
        ? formatAmountDigits(payment.total)
        : '',
    [payment?.total]
  );

  const paymentDetails = (
    <Stack bg="#FAFAFA" borderRadius="8px" px={2} py={3} spacing={1} w="full">
      <Flex align="center" gap={2} justify="space-between">
        <Text color={TEXT_PRIMARY} flex="1" fontSize="14px" lineHeight="20px">
          Pesanan dibuat:
        </Text>
        <Text
          color={TEXT_PRIMARY}
          fontSize="14px"
          lineHeight="20px"
          whiteSpace="nowrap"
        >
          {formatTransactionDate(order.transaction_date)}
        </Text>
      </Flex>
      <Flex align="center" gap={2} justify="space-between">
        <Text color={TEXT_PRIMARY} flex="1" fontSize="14px" lineHeight="20px">
          Metode pembayaran:
        </Text>
        <Text
          color={TEXT_PRIMARY}
          fontSize="14px"
          lineHeight="20px"
          whiteSpace="nowrap"
        >
          Bank Transfer
        </Text>
      </Flex>
      <Flex align="center" gap={2} justify="space-between">
        <Text color={TEXT_PRIMARY} flex="1" fontSize="14px" lineHeight="20px">
          Bank Tujuan:
        </Text>
        {bankAccount ? (
          <NextImage
            alt={bankAccount.bank_name}
            height={20}
            src={resolveBankLogo(bankAccount.bank_name)}
            width={59}
          />
        ) : (
          <Text color={ALERT_RED} fontSize="14px" lineHeight="20px">
            Data rekening tidak tersedia
          </Text>
        )}
      </Flex>
      <Stack px={2} spacing={3} w="full">
        <Flex align="center" gap={4} justify="space-between" w="full">
          <Flex align="flex-start" flex="1" gap={1} minW={0}>
            <Text
              color={TEXT_PRIMARY}
              flex="1"
              fontSize="14px"
              lineHeight="20px"
            >
              No. Rek:
            </Text>
            {bankAccount ? (
              <Stack align="flex-end" flexShrink={0} spacing="2px">
                <Text
                  color={TEXT_PRIMARY}
                  fontSize="16px"
                  fontWeight={600}
                  lineHeight="19px"
                >
                  {bankAccount.bank_account_number}
                </Text>
                <Text color={TEXT_MUTED} fontSize="10px" lineHeight="12px">
                  a.n. {bankAccount.bank_account_holder}
                </Text>
              </Stack>
            ) : (
              <Text color="#777" fontSize="14px">
                Tidak tersedia
              </Text>
            )}
          </Flex>
          {bankAccount && (
            <CopyButton
              label="Salin nomor rekening"
              onCopy={() =>
                void copyValue(
                  'Nomor rekening',
                  bankAccount.bank_account_number
                )
              }
            />
          )}
        </Flex>
        <Flex align="center" gap={4} justify="space-between" w="full">
          <Flex align="center" flex="1" gap={1} minW={0}>
            <Text
              color={TEXT_PRIMARY}
              flex="1"
              fontSize="14px"
              lineHeight="20px"
            >
              Total:
            </Text>
            {amountDigits ? (
              <HStack align="center" flexShrink={0} spacing="2px">
                <Text
                  color={TEXT_PRIMARY}
                  fontSize="14px"
                  fontWeight={400}
                  lineHeight="20px"
                >
                  Rp
                </Text>
                <Text
                  color={TEXT_PRIMARY}
                  fontSize="16px"
                  fontWeight={600}
                  lineHeight="19px"
                >
                  {amountDigits}
                </Text>
              </HStack>
            ) : (
              <Text color={TEXT_PRIMARY} fontSize="16px" fontWeight={600}>
                -
              </Text>
            )}
          </Flex>
          {payment && (
            <CopyButton
              label="Salin total"
              onCopy={() => void copyValue('Total', String(payment.total))}
            />
          )}
        </Flex>
        <Flex align="center" gap={4} justify="space-between" w="full">
          <Flex align="center" flex="1" gap={1} minW={0}>
            <Stack flex="1" spacing="2px">
              <Text
                color={TEXT_PRIMARY}
                flex="1"
                fontSize="14px"
                lineHeight="20px"
              >
                Keterangan/Berita:
              </Text>
              <Text color={ALERT_RED} fontSize="10px" lineHeight="12px">
                WAJIB ditulis pada keterangan transfer!
              </Text>
            </Stack>
            <Text
              color={TEXT_PRIMARY}
              flexShrink={0}
              fontSize="16px"
              fontWeight={600}
              lineHeight="19px"
            >
              {order.transaction_id}
            </Text>
          </Flex>
          <CopyButton
            label="Salin referensi transaksi"
            onCopy={() =>
              void copyValue('Referensi transaksi', order.transaction_id)
            }
          />
        </Flex>
      </Stack>
    </Stack>
  );

  return (
    <Box bg="#FAFAFA" minH="100vh">
      <Box>
        <Center bg="white" h={{ base: '64px', md: '80px' }}>
          <NextImage
            alt="Sukanda Onelink"
            height={44}
            priority
            quality={100}
            src="/images/sukanda_logo_navbar.png"
            style={{ height: '29px', width: 'auto' }}
            width={482}
          />
        </Center>
        <Box bgGradient={BRAND_GRADIENT} h="2px" />
      </Box>

      <Center px={4} py={{ base: 4, md: 8 }}>
        <Box
          bg="white"
          borderRadius="8px"
          boxShadow="0 4px 4px -4px rgba(12, 12, 13, 0.05), 0 16px 16px -8px rgba(12, 12, 13, 0.1)"
          maxW="640px"
          minH={{ base: 'auto', md: '644px' }}
          overflow="hidden"
          position="relative"
          w="full"
        >
          <Box h="80px" overflow="hidden" position="relative">
            <Box bgGradient={BRAND_GRADIENT} h="80px" />
            <Box
              aria-hidden="true"
              backgroundImage="url(/images/payment-instruction-header-pattern.svg)"
              backgroundRepeat="repeat"
              data-testid="payment-instruction-header-pattern"
              inset="0"
              opacity="0.1"
              pointerEvents="none"
              position="absolute"
            />
          </Box>

          <Center
            h="104px"
            left="50%"
            pointerEvents="none"
            position="absolute"
            top="24px"
            transform="translateX(-50%)"
            w="104px"
          >
            <NextImage
              alt=""
              height={177}
              priority
              quality={100}
              src="/images/payment-instruction-success.png"
              style={{ height: '104px', width: '104px' }}
              width={175}
            />
          </Center>

          <Stack
            align="stretch"
            px={{ base: 4, md: 6 }}
            pb={6}
            pt={{ base: '72px', md: '72px' }}
            spacing={6}
          >
            <Stack align="center" spacing={1}>
              <HStack spacing={2}>
                <Icon as={CheckCircle} boxSize="20px" color={TEXT_PRIMARY} />
                <Text
                  color={TEXT_PRIMARY}
                  fontSize={{ base: '18px', md: '24px' }}
                  fontWeight={500}
                  lineHeight="29px"
                >
                  Pesanan Berhasil Dibuat
                </Text>
              </HStack>
              <HStack align="center" spacing={2}>
                <Text
                  color={TEXT_PRIMARY}
                  fontSize="14px"
                  fontWeight={600}
                  lineHeight="17px"
                >
                  Batas Bank Transfer
                </Text>
                {isExpired ? (
                  <Text
                    color={ALERT_RED}
                    fontSize="16px"
                    fontWeight={600}
                    lineHeight="19px"
                  >
                    Batas transfer telah berakhir
                  </Text>
                ) : (
                  payment && (
                    <Text
                      color={ALERT_RED}
                      fontSize="16px"
                      fontWeight={600}
                      lineHeight="19px"
                    >
                      {remainingLabel}
                    </Text>
                  )
                )}
              </HStack>
            </Stack>

            <Stack
              align="stretch"
              alignSelf="center"
              data-testid={
                showProofPanel
                  ? 'payment-instruction-content-column'
                  : undefined
              }
              maxW={showProofPanel ? '496px' : 'full'}
              spacing={3}
              w="full"
            >
              {showProofPanel ? (
                <Accordion allowToggle w="full">
                  <AccordionItem border="none" w="full">
                    <AccordionButton
                      bg="#FAFAFA"
                      borderRadius="8px"
                      h="56px"
                      px={4}
                      w="full"
                      _hover={{ bg: '#FAFAFA' }}
                    >
                      <Text
                        color={TEXT_PRIMARY}
                        flex="1"
                        fontSize="14px"
                        fontWeight={600}
                        lineHeight="17px"
                        textAlign="left"
                      >
                        Info Pesanan dan Pembayaran
                      </Text>
                      <AccordionIcon />
                    </AccordionButton>
                    <AccordionPanel px={0} pt={3} pb={0} w="full">
                      {paymentDetails}
                    </AccordionPanel>
                  </AccordionItem>
                </Accordion>
              ) : (
                paymentDetails
              )}
              <Text
                color={TEXT_PRIMARY}
                fontSize="14px"
                lineHeight="20px"
                textAlign="center"
                w="full"
              >
                {showProofPanel
                  ? 'Segera lakukan pembayaran, upload dan kirim bukti transfer agar pesanan segera diproses.'
                  : 'Segera lakukan pembayaran lalu upload bukti transfer agar pesanan segera diproses.'}
              </Text>
              {proofPanel ? (
                <Box
                  aria-hidden={!showProofPanel}
                  h={showProofPanel ? 'auto' : '0'}
                  opacity={showProofPanel ? 1 : 0}
                  overflow={showProofPanel ? 'visible' : 'hidden'}
                  pointerEvents={showProofPanel ? 'auto' : 'none'}
                  position={showProofPanel ? 'relative' : 'absolute'}
                  w={showProofPanel ? 'full' : '0'}
                >
                  {proofPanel}
                </Box>
              ) : null}
            </Stack>

            <Stack align="center" spacing={6} w="full">
              {showProofPanel ? (
                <Button
                  bg="transparent"
                  color={ACCENT_BLUE}
                  fontSize="14px"
                  fontWeight={500}
                  h="16px"
                  minW="240px"
                  onClick={onDetailOrder}
                  px={0}
                  variant="unstyled"
                  _hover={{
                    bg: 'transparent',
                    textDecoration: 'underline',
                  }}
                >
                  Detail Pesanan
                </Button>
              ) : (
                <>
                  <Button
                    bg={ACCENT_BLUE}
                    borderRadius="8px"
                    color="white"
                    fontSize="14px"
                    fontWeight={500}
                    h="48px"
                    isDisabled={
                      isExpired || isUploadDisabled || !bankAccount || !payment
                    }
                    onClick={onUpload}
                    variant="solid"
                    w="240px"
                    _hover={{ bg: ACCENT_BLUE }}
                  >
                    Unggah Bukti Transfer
                  </Button>
                  <Button
                    bg="transparent"
                    color={ACCENT_BLUE}
                    fontSize="14px"
                    fontWeight={500}
                    h="16px"
                    minW="240px"
                    onClick={onDetailOrder}
                    px={0}
                    variant="unstyled"
                    _hover={{
                      bg: 'transparent',
                      textDecoration: 'underline',
                    }}
                  >
                    Detail Pesanan
                  </Button>
                </>
              )}
            </Stack>
          </Stack>
        </Box>
      </Center>
    </Box>
  );
};
